/**
 * SmartLink analytics — dash data layer.
 *
 * Thin typed wrapper over `apiClient` for the
 * `/api/v1/smartlink-analytics/:smartlink_id/summary` endpoint (feature
 * #115). Mirrors the response shape declared in
 * `apps/api/src/routes/smartlink-analytics.ts`:
 *
 *   GET /:smartlink_id/summary?from=<unix>&to=<unix>
 *     → { smartlink_id, from, to, total_clicks, total_conversions,
 *         offers: [{ offer_id, clicks, conversions, cr_bps,
 *                   traffic_share_bps }] }
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React,
 * no TanStack — so the validators can be exercised from a plain vitest
 * file without a DOM.
 */
import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Response shape — aligned with the API's `SummaryResponse`. Re-declared
// here because the dash cannot import across the `apps/` boundary in the
// 16-parallel lane model.
// ---------------------------------------------------------------------------

export interface OfferSummary {
	offer_id: string;
	clicks: number;
	conversions: number;
	/** Conversion rate in basis points (1/10000). 2500 bps = 25%. */
	cr_bps: number;
	/** Share of total clicks in basis points. 5000 bps = 50%. */
	traffic_share_bps: number;
}

export interface SummaryResponse {
	smartlink_id: string;
	from: number;
	to: number;
	total_clicks: number;
	total_conversions: number;
	offers: OfferSummary[];
}

// ---------------------------------------------------------------------------
// Date-range validator — operators fill a <input type="date"> pair, the
// component converts them into unix-second integers before querying. The
// validator keeps the form lane usable without TZ footguns: we interpret
// the `from` date as 00:00:00 UTC and the `to` date as 23:59:59 UTC so a
// same-day range includes the whole day.
// ---------------------------------------------------------------------------

export const SMARTLINK_ID_MAX = 64;
export const SMARTLINK_ID_RE = /^[a-z0-9_-]{1,64}$/i;

/**
 * ISO-date (YYYY-MM-DD) validator — strict, rejects malformed input. We
 * avoid `new Date("YYYY-MM-DD")` for pure validation because it accepts
 * garbage like "2026-13-99" in some runtimes. The regex + `Date.UTC`
 * roundtrip guarantees calendar correctness.
 */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidIsoDate(value: string): boolean {
	const match = ISO_DATE_RE.exec(value);
	if (!match) return false;
	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	if (month < 1 || month > 12) return false;
	if (day < 1 || day > 31) return false;
	const ts = Date.UTC(year, month - 1, day);
	const check = new Date(ts);
	return (
		check.getUTCFullYear() === year &&
		check.getUTCMonth() === month - 1 &&
		check.getUTCDate() === day
	);
}

export function isoDateToUnixStart(value: string): number {
	const [y, m, d] = value.split("-").map(Number);
	return Math.floor(Date.UTC(y, m - 1, d) / 1000);
}

export function isoDateToUnixEnd(value: string): number {
	const [y, m, d] = value.split("-").map(Number);
	// 23:59:59 UTC, inclusive end-of-day.
	return Math.floor(Date.UTC(y, m - 1, d, 23, 59, 59) / 1000);
}

// ---------------------------------------------------------------------------
// Hand-rolled schema — same `.safeParse` surface as the zod-free dash
// pattern used by other dash features (opD08 os-targeting).
// ---------------------------------------------------------------------------

export interface SummaryQueryInput {
	smartlinkId: string;
	from: string;
	to: string;
}

export interface SummaryQueryParsed {
	smartlinkId: string;
	from: number;
	to: number;
	fromIso: string;
	toIso: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const summaryQuerySchema = {
	safeParse(input: SummaryQueryInput): ParseResult<SummaryQueryParsed> {
		const errors: Record<string, string> = {};

		const smartlinkId = (input.smartlinkId ?? "").trim();
		if (smartlinkId.length === 0) {
			errors.smartlinkId = "SmartLink ID is required.";
		} else if (smartlinkId.length > SMARTLINK_ID_MAX) {
			errors.smartlinkId = `SmartLink ID must be ${SMARTLINK_ID_MAX} chars or fewer.`;
		} else if (!SMARTLINK_ID_RE.test(smartlinkId)) {
			errors.smartlinkId =
				"SmartLink ID may contain letters, digits, dashes, and underscores.";
		}

		const fromIso = (input.from ?? "").trim();
		if (fromIso.length === 0) {
			errors.from = "Start date is required.";
		} else if (!isValidIsoDate(fromIso)) {
			errors.from = "Start date must be a valid YYYY-MM-DD.";
		}

		const toIso = (input.to ?? "").trim();
		if (toIso.length === 0) {
			errors.to = "End date is required.";
		} else if (!isValidIsoDate(toIso)) {
			errors.to = "End date must be a valid YYYY-MM-DD.";
		}

		// Only compare when both sides parsed cleanly — avoid fake NaN errors.
		if (!errors.from && !errors.to) {
			const fromUnix = isoDateToUnixStart(fromIso);
			const toUnix = isoDateToUnixEnd(toIso);
			if (fromUnix > toUnix) {
				errors.to = "End date must be on or after the start date.";
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}

		return {
			success: true,
			data: {
				smartlinkId,
				from: isoDateToUnixStart(fromIso),
				to: isoDateToUnixEnd(toIso),
				fromIso,
				toIso,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Formatting helpers — the table surfaces cr_bps / share_bps as human
// percentages. 10_000 bps = 100%, so a divide-by-100 yields percent.
// ---------------------------------------------------------------------------

export function bpsToPercent(bps: number): number {
	if (!Number.isFinite(bps)) return 0;
	return bps / 100;
}

export function formatCrPercent(bps: number): string {
	return `${bpsToPercent(bps).toFixed(2)}%`;
}

export function formatSharePercent(bps: number): string {
	return `${bpsToPercent(bps).toFixed(2)}%`;
}

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return n.toLocaleString("en-US");
}

/**
 * Share-bar width as a percent string usable directly in a `style={{ width }}`
 * declaration. Guards against negative or overflowed values so the bar never
 * stretches beyond its rail.
 */
export function shareBarWidth(bps: number): string {
	const pct = bpsToPercent(bps);
	const clamped = Math.max(0, Math.min(100, pct));
	return `${clamped.toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/smartlink-analytics";

/**
 * Fetch the per-offer summary for a smartlink in the given window. 404 is
 * normalised to `null` so the table component can render an empty state
 * without an error toast.
 */
export async function fetchSmartlinkSummary(args: {
	smartlinkId: string;
	from: number;
	to: number;
}): Promise<SummaryResponse | null> {
	try {
		return await apiClient.get<SummaryResponse>(
			`${BASE}/${encodeURIComponent(args.smartlinkId)}/summary`,
			{ query: { from: args.from, to: args.to } },
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export const smartlinkAnalyticsQueryKeys = {
	all: ["smartlink-analytics"] as const,
	summary: (smartlinkId: string, from: number, to: number) =>
		[
			...smartlinkAnalyticsQueryKeys.all,
			"summary",
			smartlinkId,
			from,
			to,
		] as const,
};
